import { RuntimeError } from '@multiplier/lib-shared-errors';
import { useOktaAuth } from '@okta/okta-react';
import * as Sentry from '@sentry/react';
import { createContext, type ReactNode, useContext, useEffect } from 'react';
import { ORG_SELECTION_STORAGE_KEY } from '@/contexts/OrganizationContext';
import { type UserResponse, useUser } from '@/hooks/queries/use-user';
import { showErrorToast } from '@/lib/error-toast';
import { queryClient } from '@/lib/query-client-config';

interface AuthContextType {
  user: UserResponse | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { oktaAuth, authState } = useOktaAuth();
  const { data: user, isPending } = useUser();

  // Set Sentry user context when user data changes
  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.sub,
        email: typeof user.email === 'string' ? user.email : undefined,
        username: typeof user.name === 'string' ? user.name : undefined,
      });
    } else {
      // Clear user context on logout
      Sentry.setUser(null);
    }
  }, [user]);

  const isAuthenticated = authState?.isAuthenticated ?? false;

  const login = async () => {
    try {
      await oktaAuth.signInWithRedirect();
    } catch (_error) {
      Sentry.captureException(_error);
      showErrorToast(_error, 'Login failed. Please try again.');
    }
  };

  const logout = async () => {
    // Clear org selection so the next user doesn't inherit a previous user's org
    localStorage.removeItem(ORG_SELECTION_STORAGE_KEY);
    // Wipe React Query cache so stale user data isn't visible to the next session
    queryClient.clear();
    try {
      await oktaAuth.tokenManager.clear();
      await oktaAuth.signOut();
    } catch (_error) {
      Sentry.captureException(_error);
      window.location.href = '/login';
    }
  };

  const getToken = async (): Promise<string | null> => {
    const accessToken = oktaAuth.getAccessToken();
    return accessToken ?? null;
  };

  const value: AuthContextType = {
    user: user ?? null,
    // Only block the route on the initial user load. A background /api/user
    // refetch sets isFetching, but not isPending; treating that as loading
    // would make SecureRoute swap to its spinner and unmount in-progress pages.
    isLoading: !authState || (isAuthenticated && isPending),
    isAuthenticated,
    login,
    logout,
    getToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new RuntimeError('useAuth must be used within an AuthProvider');
  }
  return context;
}
